"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { templateFieldState } from "@/lib/categories/inheritance";
import { InfoHint } from "./info-hint";
import type {
  TitlePlaceholders,
  TitleTemplateInfo,
} from "@/services/category.service";

/** Plain names for the building blocks; unmapped keys fall back to the key. */
const BLOCK_NAMES: Record<string, string> = {
  brand: "Brand",
  product_type: "Product type",
  material: "Material",
  colour: "Colour",
  color: "Colour",
  style: "Style",
  width_cm: "Width",
  key_feature: "Key feature",
  key_numeric_details: "Key details",
};

const SAMPLE: Record<string, string> = {
  brand: "Aster",
  product_type: "Three-seat Sofa",
  material: "bouclé",
  colour: "Sand",
  color: "Sand",
  style: "Mid-century",
  width_cm: "212",
};

interface TitleTemplateFieldProps {
  info: TitleTemplateInfo | null;
  placeholders: TitlePlaceholders | null;
  /** The category's own template; empty means it is inheriting. */
  value: string;
  hasOwn: boolean;
  readOnly: boolean;
  /**
   * The catalogue-wide scope, which is fixed to the system pattern. Kept
   * separate from `inheritedFrom` so a top-level category stays editable.
   */
  isCatalogueWide: boolean;
  /** Where the pattern comes from, or null when it is the system default. */
  inheritedFrom: string | null;
  onChange: (value: string) => void;
  onCustomise: () => void;
  onReset: () => void;
}

export function TitleTemplateField({
  info,
  placeholders,
  value,
  hasOwn,
  readOnly,
  isCatalogueWide,
  inheritedFrom,
  onChange,
  onCustomise,
  onReset,
}: TitleTemplateFieldProps) {
  const fieldState = templateFieldState({ isCatalogueWide, hasOwn, inheritedFrom });
  const resolved = info?.resolved || placeholders?.systemDefault || "";
  const shown = hasOwn ? value : resolved;

  const preview = shown
    .split(/(\{\w+\})/g)
    .map((part, i) => {
      const match = /^\{(\w+)\}$/.exec(part);
      if (!match) return <span key={i}>{part}</span>;
      return (
        <em
          key={i}
          className="rounded bg-pink-50 px-1 not-italic text-pink-700"
        >
          {SAMPLE[match[1]] ?? match[1]}
        </em>
      );
    });

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          How titles are written
          <InfoHint>
            The title template for this category — the pattern Youzu follows when it
            names products here. Click a building block below to drop it in; Youzu swaps
            each one for the real value on each product.
          </InfoHint>
        </h3>
        {(fieldState === "inherited" || fieldState === "none" || fieldState === "catalogue-wide") && !readOnly && !hasOwn && (
          <Button variant="outline" size="sm" className="h-7" onClick={onCustomise}>
            {fieldState === "catalogue-wide" ? "Set catalogue pattern" : "Write titles differently here"}
          </Button>
        )}
      </div>

      <Input
        value={shown}
        readOnly={!hasOwn}
        disabled={readOnly}
        placeholder="e.g. {brand} {product_type} — {colour}"
        className={
          hasOwn
            ? "font-mono text-[13px]"
            : "border-dashed bg-muted/40 font-mono text-[13px] text-muted-foreground"
        }
        onChange={(e) => onChange(e.target.value)}
      />

      <p className="text-xs text-muted-foreground">
        {fieldState === "catalogue-wide" ? (
          <>
            This is the standard pattern Youzu uses when no category overrides it.
            {!readOnly && hasOwn && (
              <button
                type="button"
                className="ml-1 font-medium text-pink-700 underline underline-offset-2 hover:text-foreground"
                onClick={onReset}
              >
                Restore the system pattern
              </button>
            )}
          </>
        ) : fieldState === "own" ? (
          <>
            Written this way just for this category.{" "}
            {!readOnly && (
              <button
                type="button"
                className="font-medium text-pink-700 underline underline-offset-2 hover:text-foreground"
                onClick={onReset}
              >
                Go back to {inheritedFrom ?? "the standard pattern"}
              </button>
            )}
          </>
        ) : (
          <>
            Same as{" "}
            <strong className="text-foreground">
              {inheritedFrom ?? "the standard pattern"}
            </strong>
            .
          </>
        )}
      </p>

      {hasOwn && placeholders && !readOnly && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">
            Click to add a building block:
          </span>
          {placeholders.placeholders.map((key) => (
            <button
              key={key}
              type="button"
              title={`Adds {${key}}`}
              className="rounded border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground hover:border-pink-300 hover:bg-pink-50 hover:text-pink-700"
              onClick={() => onChange(`${value}{${key}}`)}
            >
              {BLOCK_NAMES[key] ?? key}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-md border bg-muted/30 px-3 py-2.5">
        <p className="mb-1 text-[10.5px] uppercase tracking-wide text-muted-foreground">
          A title would look like this
        </p>
        <p className="text-[15px]">
          {shown ? preview : <span className="text-muted-foreground">Nothing set.</span>}
        </p>
      </div>
    </section>
  );
}
